import { ApiError } from '@/shared/api/http';

/* The presentation decisions behind forgetting and resetting a password, separated from the
 * rendering so they are testable without a browser and so two screens cannot disagree about what a
 * state means.
 *
 * Three distinctions carry this file, and every one of them is a case where collapsing two facts
 * into one would produce a screen that is confidently wrong:
 *
 *   * **A link with no token is not a link with a rejected token.** The first means the address was
 *     mangled on its way here — truncated by a mail client, copied without its query string — and
 *     the reader should go back and ask for another. The second means the platform read a token and
 *     refused it, which is almost always expiry. Telling somebody their link expired when it never
 *     arrived intact sends them to wait for a second mail that will fail the same way.
 *   * **A request that was accepted is not a request that was delivered.** `transport: "none"` is a
 *     202 for a deployment with no mail configured, so the reset exists and nothing can carry it.
 *   * **A refused reset is either about the token or about the password**, and the two are fixed by
 *     different people doing different things.
 */

/** The minimum the server enforces, mirrored so the common case is caught before a round trip.
 *
 * Mirrors `MIN_PASSWORD_LENGTH` in `core.tenancy.domain.local_credential`. **Never the authority** —
 * the server refuses a short password regardless of what this file checks, and answers 422 on the
 * reset route with its own sentence. Declared here rather than in each form because `SignupForm` had
 * its own copy and a second constant for one rule is how the sign-up hint and the reset hint come to
 * state different numbers.
 */
export const MIN_PASSWORD_LENGTH = 10;

/** The token out of a reset link, or its absence. */
export type ResetToken =
  /** No `token` parameter, or an empty one. A fact about the *link*, not about the token. */
  { readonly kind: 'missing' } | { readonly kind: 'present'; readonly token: string };

/** Reads the token out a `?token=…` query string.
 *
 * Trimmed, and whitespace-only counts as missing: a token pasted out of a mail client routinely
 * arrives with a trailing newline, and a token that is nothing but whitespace is a mangled link
 * rather than a value worth spending a round trip on.
 *
 * `URLSearchParams` rather than a split on `=`, because a base64url token is exactly the shape that
 * carries characters a hand-rolled parser mishandles.
 */
export function tokenFromQuery(search: string): ResetToken {
  const raw = new URLSearchParams(search).get('token');
  if (raw === null) return { kind: 'missing' };
  const token = raw.trim();
  return token === '' ? { kind: 'missing' } : { kind: 'present', token };
}

/** Whether a reset request the server accepted can actually reach anybody.
 *
 * The route always answers 202 — deliberately, because answering differently for a known and an
 * unknown address is an email-enumeration oracle — so `transport` is the only thing on the wire that
 * distinguishes "a link is on its way" from "this deployment cannot send one".
 */
export type Delivery = 'sent' | 'undeliverable';

/** **Anything other than a transport we recognise reads as undeliverable, and that direction is
 * deliberate.**
 *
 * The two mistakes are not symmetric. Telling somebody to ask an administrator when a mail did in
 * fact arrive costs them a minute; telling somebody to check their inbox when nothing was sent
 * leaves them waiting for a mail nobody can send, which is the worst outcome this screen has. So an
 * unrecognised value fails toward saying so, the same way an unmeasured figure on this platform is
 * never rendered as a zero.
 */
export function deliveryOf(transport: string): Delivery {
  return transport === 'email' ? 'sent' : 'undeliverable';
}

/** Which refusal a failed reset is, so the reader is sent to the thing that will fix it. */
export type ResetRefusal =
  /** 400: unknown, expired or already spent. One indistinguishable answer by design. */
  | 'token'
  /** 422: the password itself. The server's own sentence says which rule it failed. */
  | 'password'
  /** Anything else — a 500, a proxy's HTML, an unreachable API. Not the reader's doing. */
  | 'other';

const BAD_REQUEST = 400;
const UNPROCESSABLE = 422;

export function resetRefusal(error: Error): ResetRefusal {
  /* Keyed on the status rather than on the message text. The server's `detail` is host-written and
   * worth showing verbatim, and matching against it would make this branch depend on copy somebody
   * is free to edit in another repository. */
  if (!(error instanceof ApiError)) return 'other';
  if (error.status === BAD_REQUEST) return 'token';
  if (error.status === UNPROCESSABLE) return 'password';
  return 'other';
}

/** What a reader must fix before the form is worth submitting, as a copy key.
 *
 * A key rather than a sentence, so no English lives in a `.ts` and the copy sweep can enumerate what
 * this feature asks for. `null` means nothing is wrong *yet* — which is not the same as valid, since
 * an empty pair is merely unfinished, and a form that turns red before it has been filled in teaches
 * a reader to ignore red.
 *
 * **Mismatch is reported before length**, because it is the one the reader can see is true. Being
 * told a password is too short while the box underneath also disagrees with it invites fixing the
 * length in one field and leaving the pair broken.
 */
export function passwordProblemKey(
  password: string,
  confirmation: string,
): string | null {
  if (password === '' && confirmation === '') return null;
  if (confirmation !== '' && password !== confirmation) return 'auth.reset.mismatch';
  if (password.length > 0 && password.length < MIN_PASSWORD_LENGTH) {
    return 'auth.reset.tooShort';
  }
  return null;
}

/** Whether the pair is complete enough to send. Separate from `passwordProblemKey` because "nothing
 * is wrong" and "there is something here" are different questions, and a pristine form is both
 * problem-free and not submittable. */
export function isSubmittable(password: string, confirmation: string): boolean {
  return password.length >= MIN_PASSWORD_LENGTH && password === confirmation;
}

/** Whether a navigation carried word of a completed password change.
 *
 * The reset screen redirects to `/login` and the confirmation travels in the router's own state
 * rather than in the query string, so a bookmark of `/login` cannot claim a password change that
 * never happened. Guarded rather than cast because `location.state` is whatever the previous screen
 * put there — including `null` on a cold load, and anything at all after a Back button.
 */
export function announcesPasswordChange(state: unknown): boolean {
  if (typeof state !== 'object' || state === null) return false;
  return (state as Record<string, unknown>)['passwordChanged'] === true;
}
